export type PodcastHistoryMode = "full_history" | "new_episodes_only";

export interface SubscribeFeedPayload {
  feed_url: string;
  notify_enabled: boolean;
  history_mode: PodcastHistoryMode;
}

export function buildSubscribeFeedPayload(
  feedUrl: string,
  notifyEnabled: boolean,
  historyMode: PodcastHistoryMode,
): SubscribeFeedPayload {
  return {
    feed_url: feedUrl.trim(),
    notify_enabled: notifyEnabled,
    history_mode: historyMode,
  };
}

export function subscriptionIdempotencyMaterial(
  feedUrl: string,
  historyMode: PodcastHistoryMode,
): string {
  return `${feedUrl.trim()}\n${historyMode}`;
}

export async function deriveSubscriptionIdempotencyKey(
  feedUrl: string,
  historyMode: PodcastHistoryMode,
): Promise<string> {
  const payload = subscriptionIdempotencyMaterial(feedUrl, historyMode);
  const encoder = new TextEncoder();
  const globalCrypto: Crypto | undefined =
    typeof window !== "undefined" && window.crypto
      ? window.crypto
      : typeof crypto !== "undefined"
        ? crypto
        : undefined;

  if (globalCrypto?.subtle) {
    try {
      const data = encoder.encode(payload);
      const digest = await globalCrypto.subtle.digest("SHA-256", data);
      return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, "0")).join("");
    } catch (err) {
      console.warn("Failed to derive SHA-256 idempotency key, falling back to legacy hash", err);
    }
  }

  let hash = 0x811c9dc5;
  for (let index = 0; index < payload.length; index += 1) {
    hash ^= payload.charCodeAt(index);
    hash = (hash * 0x01000193) >>> 0;
  }
  return `rss-${hash.toString(16).padStart(8, "0")}`;
}

export function requiresBackCatalogConfirmation(historyMode: PodcastHistoryMode): boolean {
  return historyMode === "full_history";
}

export function formatSubscriptionHistoryPolicy(
  historyMode?: PodcastHistoryMode | null,
  importStartDate?: string | null,
): string {
  if (historyMode !== "new_episodes_only") {
    return "Full history";
  }

  if (!importStartDate) {
    return "Following new episodes only";
  }

  const parsed = new Date(importStartDate);
  if (Number.isNaN(parsed.getTime())) {
    return "Following new episodes only";
  }

  return `Following since ${parsed.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  })}`;
}
